import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from 'react-toastify';

// Internal ApexChart wrapper using the template's window.ApexCharts
function ApexChart({ options, series, type = 'line', height = 300 }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !window.ApexCharts) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const mergedOptions = {
      ...options,
      chart: {
        ...(options.chart || {}),
        type,
        height,
        toolbar: { show: false },
        fontFamily: 'Nunito, sans-serif',
      },
      series,
    };

    chartInstance.current = new window.ApexCharts(chartRef.current, mergedOptions);
    chartInstance.current.render();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [options, series, type, height]);

  return <div ref={chartRef} />;
}

function Index() {
  const authToken = sessionStorage.getItem('authToken');
  const baseurl = process.env.REACT_APP_BASE_API_URL;

  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalUsers: 0,
      totalDeposits: 0,
      totalWithdrawals: 0,
      rewardsDistributed: 0,
      totalSalaryPaid: 0,
      activeUsers: 0,
      pendingWithdrawals: 0,
      monthlyRevenue: 0,
    },
    lineChartData: [],
    pieChartData: [],
    recentTransactions: [],
    recentUsers: [],
  });

  // Fetch Dashboard Statics
  const fetchDashboardData = useCallback(async () => {
    try {
      if (!authToken) return;

      const response = await fetch(`${baseurl}/statics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 429) {
        return;
      }

      const result = await response.json();

      if (result.status) {
        setDashboardData({
          stats: {
            totalUsers: result.stats?.totalUsers ?? result.total_clients ?? 0,
            totalDeposits: result.stats?.totalDeposits ?? result.total_deposits ?? 0,
            totalWithdrawals: result.stats?.totalWithdrawals ?? result.no_of_withdraw ?? 0,
            rewardsDistributed: result.stats?.rewardsDistributed ?? 0,
            totalSalaryPaid: result.stats?.totalSalaryPaid ?? 0,
            activeUsers: result.stats?.activeUsers ?? result.no_of_active ?? 0,
            pendingWithdrawals: result.stats?.pendingWithdrawals ?? 0,
            monthlyRevenue: result.stats?.monthlyRevenue ?? 0,
          },
          lineChartData: Array.isArray(result.lineChartData) ? result.lineChartData : [],
          pieChartData: Array.isArray(result.pieChartData) ? result.pieChartData : [],
          recentTransactions: Array.isArray(result.recentTransactions) ? result.recentTransactions : [],
          recentUsers: Array.isArray(result.recentUsers) ? result.recentUsers : [],
        });
      }
    } catch (error) {
      console.error('Fetch statics error:', error);
    }
  }, [authToken, baseurl]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const copyToClipboard = (text, label = 'Wallet') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} address copied!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatShortWallet = (wallet) => {
    if (!wallet) return 'N/A';
    if (wallet.length <= 14) return wallet;
    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  };

  // Apex Area/Line Chart Options
  const lineChartCategories = useMemo(() => {
    return dashboardData.lineChartData.map((d) => d.name || '');
  }, [dashboardData.lineChartData]);

  const lineChartSeries = useMemo(() => {
    return [
      {
        name: 'Deposits ($)',
        data: dashboardData.lineChartData.map((d) => Number(d.deposits || 0)),
      },
      {
        name: 'Withdrawals ($)',
        data: dashboardData.lineChartData.map((d) => Number(d.withdrawals || 0)),
      },
    ];
  }, [dashboardData.lineChartData]);

  const lineChartOptions = useMemo(() => {
    return {
      colors: ['#0162e8', '#f93a5a'],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          opacityFrom: 0.45,
          opacityTo: 0.05,
        },
      },
      xaxis: {
        categories: lineChartCategories,
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: (val) => `$${Number(val).toLocaleString()}`,
        },
      },
      grid: { borderColor: '#f1f1f1' },
      legend: { position: 'top', horizontalAlign: 'right' },
      tooltip: {
        y: {
          formatter: (val) => `$${Number(val).toLocaleString()}`,
        },
      },
    };
  }, [lineChartCategories]);

  // Apex Donut/Pie Chart Options
  const pieChartLabels = useMemo(() => {
    return dashboardData.pieChartData.map((d) => d.name || '');
  }, [dashboardData.pieChartData]);

  const pieChartSeries = useMemo(() => {
    return dashboardData.pieChartData.map((d) => Number(d.value || 0));
  }, [dashboardData.pieChartData]);

  const pieChartOptions = useMemo(() => {
    return {
      labels: pieChartLabels,
      colors: ['#0162e8', '#f93a5a', '#f7a556', '#22c55e'],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true },
      stroke: { width: 0 },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Users',
                formatter: () => `${dashboardData.stats.totalUsers || 0}`,
              },
            },
          },
        },
      },
    };
  }, [pieChartLabels, dashboardData.stats.totalUsers]);

  const { stats, recentTransactions, recentUsers } = dashboardData;

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">

            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="mb-0 text-dark fw-bold">Hi, welcome back!</h4>
                <p className="text-muted mb-0">Overview of platform activities, real-time analytics and financial metrics</p>
              </div>
              <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={fetchDashboardData}
                  title="Refresh statistics"
                >
                  <i className="fa fa-sync-alt"></i> Refresh Data
                </button>
              </div>
            </div>

            {/* Top 4 KPI Cards */}
            <div className="row">
              {/* Card 1: Total Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div className="card overflow-hidden sales-card bg-primary-gradient border-0 shadow-sm">
                  <div className="px-3 pt-3 pb-2">
                    <div>
                      <h6 className="mb-3 fs-12 text-fixed-white">TOTAL USERS</h6>
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(stats.totalUsers || 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Registered clients
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-users text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Total Deposits */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div className="card overflow-hidden sales-card bg-danger-gradient border-0 shadow-sm">
                  <div className="px-3 pt-3 pb-2">
                    <div>
                      <h6 className="mb-3 fs-12 text-fixed-white">TOTAL STAKES</h6>
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            ${Number(stats.totalDeposits || 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Platform deposit volume
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-arrow-circle-down text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Active Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div className="card overflow-hidden sales-card bg-success-gradient border-0 shadow-sm">
                  <div className="px-3 pt-3 pb-2">
                    <div>
                      <h6 className="mb-3 fs-12 text-fixed-white">ACTIVE USERS</h6>
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(stats.activeUsers || 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Currently active clients
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-user-check text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Total Withdrawals */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div className="card overflow-hidden sales-card bg-warning-gradient border-0 shadow-sm">
                  <div className="px-3 pt-3 pb-2">
                    <div>
                      <h6 className="mb-3 fs-12 text-fixed-white">TOTAL WITHDRAWALS</h6>
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(stats.totalWithdrawals || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Payout operations
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-money-bill-wave text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Metrics Strip */}
            <div className="row g-3 mb-4">
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 mb-0">
                  <div className="card-body p-3 d-flex align-items-center justify-content-between">
                    <div>
                      <span className="text-muted fs-12 fw-medium text-uppercase">Rewards Distributed</span>
                      <h5 className="fw-bold mb-0 text-dark mt-1">
                        ${Number(stats.rewardsDistributed || 0).toLocaleString()}
                      </h5>
                    </div>
                    <div className="rounded-circle bg-primary-subtle text-primary p-2">
                      <i className="fas fa-gift fs-18"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 mb-0">
                  <div className="card-body p-3 d-flex align-items-center justify-content-between">
                    <div>
                      <span className="text-muted fs-12 fw-medium text-uppercase">Total Salary Paid</span>
                      <h5 className="fw-bold mb-0 text-dark mt-1">
                        ${Number(stats.totalSalaryPaid || 0).toLocaleString()}
                      </h5>
                    </div>
                    <div className="rounded-circle bg-success-subtle text-success p-2">
                      <i className="fas fa-wallet fs-18"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 mb-0">
                  <div className="card-body p-3 d-flex align-items-center justify-content-between">
                    <div>
                      <span className="text-muted fs-12 fw-medium text-uppercase">Pending Withdrawals</span>
                      <h5 className="fw-bold mb-0 text-dark mt-1">
                        {Number(stats.pendingWithdrawals || 0)}
                      </h5>
                    </div>
                    <div className="rounded-circle bg-warning-subtle text-warning p-2">
                      <i className="fas fa-clock fs-18"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 mb-0">
                  <div className="card-body p-3 d-flex align-items-center justify-content-between">
                    <div>
                      <span className="text-muted fs-12 fw-medium text-uppercase">Monthly Revenue</span>
                      <h5 className="fw-bold mb-0 text-dark mt-1">
                        ${Number(stats.monthlyRevenue || 0).toLocaleString()}
                      </h5>
                    </div>
                    <div className="rounded-circle bg-info-subtle text-info p-2">
                      <i className="fas fa-chart-pie fs-18"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Charts Row */}
            <div className="row">
              {/* Deposits vs Withdrawals Area Chart */}
              <div className="col-xl-8 col-lg-12 col-md-12">
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                        <i className="fas fa-chart-area text-primary"></i>
                        <span>Deposits vs Withdrawals Overview</span>
                      </h6>
                      <p className="text-muted mb-0 fs-12">Monthly deposit volume compared to withdrawals</p>
                    </div>
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                      7 Months Trend
                    </span>
                  </div>
                  <div className="card-body p-3">
                    <ApexChart
                      options={lineChartOptions}
                      series={lineChartSeries}
                      type="area"
                      height={290}
                    />
                  </div>
                </div>
              </div>

              {/* User Distribution Donut Chart */}
              <div className="col-xl-4 col-lg-12 col-md-12">
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                        <i className="fas fa-chart-pie text-info"></i>
                        <span>User Demographics</span>
                      </h6>
                      <p className="text-muted mb-0 fs-12">Distribution by user engagement</p>
                    </div>
                  </div>
                  <div className="card-body p-3">
                    <ApexChart
                      options={pieChartOptions}
                      series={pieChartSeries}
                      type="donut"
                      height={290}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Users & Transactions Tables Row */}
            <div className="row">
              {/* Recent Users Card */}
              <div className="col-xl-5 col-md-12 col-lg-12">
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                      <i className="fas fa-user-clock text-primary"></i>
                      <span>Recent Users</span>
                    </h6>
                    <span className="badge bg-light text-muted border fs-11">
                      {recentUsers.length} Users
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 text-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>Wallet Address</th>
                          <th>Deposit</th>
                          <th>Status</th>
                          <th className="text-end">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentUsers.length > 0 ? (
                          recentUsers.map((u, index) => (
                            <tr key={u.id || index}>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="font-monospace text-dark fs-13" title={u.wallet}>
                                    {formatShortWallet(u.wallet)}
                                  </span>
                                  {u.wallet && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-muted ms-1"
                                      onClick={() => copyToClipboard(u.wallet, 'User wallet')}
                                      title="Copy wallet address"
                                    >
                                      <i className="fas fa-copy fs-11"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="fw-semibold text-dark">
                                  {Number(u.deposit || 0).toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${u.status === 'Active' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-secondary-subtle text-secondary'}`}>
                                  {u.status || 'Active'}
                                </span>
                              </td>
                              <td className="text-end text-muted fs-12">
                                {u.joined || 'N/A'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-4 text-muted">
                              No recent users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Recent Transactions Card */}
              <div className="col-xl-7 col-md-12 col-lg-12">
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                      <i className="fas fa-exchange-alt text-warning"></i>
                      <span>Recent Transactions</span>
                    </h6>
                    <span className="badge bg-light text-muted border fs-11">
                      {recentTransactions.length} Transactions
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 text-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Type</th>
                          <th>User / Wallet</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th className="text-end">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTransactions.length > 0 ? (
                          recentTransactions.map((tx, index) => {
                            const isWithdrawal = tx.type === 'Withdrawal';
                            const amountVal = tx.amount !== undefined ? tx.amount : tx.deposit;
                            const userLabel = tx.user || formatShortWallet(tx.wallet);

                            return (
                              <tr key={tx.id || index}>
                                <td>
                                  <span className="text-muted fs-12 fw-medium">#{tx.id || 'N/A'}</span>
                                </td>
                                <td>
                                  <span className={`badge ${isWithdrawal ? 'bg-warning-subtle text-warning border border-warning-subtle' : 'bg-primary-subtle text-primary border border-primary-subtle'}`}>
                                    <i className={`fas ${isWithdrawal ? 'fa-arrow-up' : 'fa-arrow-down'} me-1`}></i>
                                    {isWithdrawal ? 'Withdrawal' : 'Deposit'}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <span className="text-dark fw-medium fs-13" title={tx.wallet || tx.user}>
                                      {userLabel}
                                    </span>
                                    {tx.wallet && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-link p-0 text-muted ms-1"
                                        onClick={() => copyToClipboard(tx.wallet, 'Wallet')}
                                        title="Copy wallet address"
                                      >
                                        <i className="fas fa-copy fs-11"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span className={`fw-bold ${isWithdrawal ? 'text-danger' : 'text-success'}`}>
                                    {isWithdrawal ? '-' : '+ $'}{Number(amountVal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${tx.status === 'Success' ? 'bg-success' : 'bg-secondary'}`}>
                                    {tx.status || 'Success'}
                                  </span>
                                </td>
                                <td className="text-end text-muted fs-12">
                                  {tx.date || 'N/A'}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              No recent transactions found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        <ToastContainer />
      </Header>
      <Footer />
    </>
  );
}

export default Index;